import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { analyticsApi, postApi, analyzeApi, gapsApi, rankApi, ideasApi } from './services/api';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Fab,
  Snackbar,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth from './components/RequireAuth';
import SEO from './components/SEO';
import MainLayout from './components/MainLayout';
import Dashboard from './components/dashboard';
import UpgradeModal from './components/UpgradeModal';
import AnalyzeContent from './components/AnalyzeContent';
import RankContent from './components/RankContent';
import TrendingNow from './components/trends';
import PostManagementPage from './pages/PostManagementPage';
import FindGaps from './components/FindGaps';
import GenerateIdeas from './components/GenerateIdeas';
import EngagementHub from './components/EngagementHub';
import LeadCRM from './components/LeadCRM';
import Guide from './components/Guide';
import Settings from './components/Settings';
import Pricing from './components/Pricing';
import Billing from './components/Billing';
import UploadTracker from './components/media/UploadTracker';

// Admin Pages & Providers
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';
import RequireSuperAdmin from './components/common/RequireSuperAdmin';
import AdminToggles from './pages/AdminToggles';

// Public Pages
import LandingPage from './components/public/LandingPage';
import Terms from './components/public/Terms';
import Privacy from './components/public/Privacy';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Data imported from backend conditionally

// Create light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#7c3aed',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Roboto", sans-serif',
    h1: { fontWeight: 900, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: "#cbd5e1 transparent",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#cbd5e1",
            minHeight: 24,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, padding: '10px 20px' },
        contained: { boxShadow: '0 4px 12px 0 rgba(79, 70, 229, 0.2)' },
      },
    },
  },
});

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#818cf8',
      light: '#a5b4fc',
      dark: '#6366f1',
    },
    secondary: {
      main: '#a78bfa',
      light: '#c4b5fd',
      dark: '#8b5cf6',
    },
    background: {
      default: '#020617',
      paper: '#0f172a',
    },
    text: {
      primary: '#f8fafc',
      secondary: '#94a3b8',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Roboto", sans-serif',
    h1: { fontWeight: 900, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: "#334155 transparent",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#334155",
            minHeight: 24,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, padding: '10px 20px' },
        contained: { boxShadow: '0 4px 12px 0 rgba(129, 140, 248, 0.2)' },
      },
    },
  },
});

// Utility to scroll to top and clear state on navigation
const LocationWatcher = ({ clearError }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    clearError();
  }, [pathname, clearError]);
  return null;
};

function App() {
  const [analytics, setAnalytics] = useState([]);
  const [analysis, setAnalysis] = useState({});
  const [ranked, setRanked] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [idea, setIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleError = useCallback((err) => {
    if (err.isQuotaError) {
      setShowUpgradeModal(true);
      setError('');
    } else if (err.isFeatureDisabled) {
      // Feature is intentionally disabled — silently swallow, page shows empty state
    } else {
      setError(err.message || 'An unexpected error occurred.');
    }
  }, []);

  const theme = isDarkMode ? darkTheme : lightTheme;

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await analyticsApi.getAll();
      setAnalytics(data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchAnalytics();
      }
    });
    return () => unsubscribe();
  }, [fetchAnalytics]);

  /**
   * handlePost — called by PostContent with per-platform media files.
   *
   * mediaFiles = { common: File|null, [platform]: File|null }
   *   - "common" is the shared fallback file
   *   - per-platform keys override the shared file for that specific platform
   *
   * Returns the results[] array so PostContent can update the modal.
   * Does NOT manage loading state (PostContent owns posting UX).
   */
  const handlePost = useCallback(async (posts, mediaFiles = {}) => {
    setError('');
    try {
      // Build multipart/form-data
      const formData = new FormData();

      // Strip File objects before serialising posts to JSON
      const postsData = posts.map(p => ({ platform: p.platform, content: p.content }));
      formData.append('posts', JSON.stringify(postsData));

      // Shared / fallback media file
      if (mediaFiles.common) {
        formData.append('media', mediaFiles.common);
      }

      // Per-platform media overrides: appended as media_youtube, media_twitter, etc.
      for (const [key, file] of Object.entries(mediaFiles)) {
        if (key !== 'common' && file instanceof File) {
          formData.append(`media_${key}`, file);
        }
      }

      const { results } = await postApi.post(formData);

      // Show top-level snackbar summary
      const successCount = results.filter(r => r.status === 'success').length;
      if (successCount > 0) {
        setSuccessMessage(
          `Posted to ${successCount} platform${successCount > 1 ? 's' : ''}!`
        );
      }

      // Return results so PostContent can update the modal
      return results;
    } catch (err) {
      console.error('Error posting:', err);
      handleError(err);
      return [];
    }
  }, [handleError]);

  const handleAnalyze = useCallback(async (textInput) => {
    setLoading(true);
    setError('');
    try {
      const data = await analyzeApi.analyze(textInput);
      setAnalysis(data);
    } catch (error) {
      console.error('Error analyzing:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const handleRank = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await rankApi.getRanked();
      setRanked(data);
    } catch (error) {
      console.error('Error ranking:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const handleGaps = useCallback(async (currentContent, topContent) => {
    setLoading(true);
    setError('');
    try {
      // Store the FULL structured result — FindGaps renders all dimensions
      const data = await gapsApi.analyze(currentContent, topContent);
      setGaps(data);
    } catch (error) {
      console.error('Error finding gaps:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const handleIdeas = useCallback(async (topic, platform = null) => {
    setLoading(true);
    setError('');
    try {
      const data = await ideasApi.generate(topic, platform, 5);
      setIdea(data);   // full { ideas, topic, platform } object
    } catch (error) {
      console.error('Error generating ideas:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const handleCloseSnackbar = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const clearGlobalError = useCallback(() => {
    setError('');
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <LocationWatcher clearError={clearGlobalError} />
          <Routes>
            {/* Public Marketing Routes */}
            <Route path="/" element={<><SEO path="/" /><LandingPage /></>} />
            <Route path="/terms" element={<><SEO title="Terms of Service | Socialixer" description="Socialixer Terms of Service — read our terms for using the AI social media automation platform." path="/terms" noIndex={false} /><Terms /></>} />
            <Route path="/privacy" element={<><SEO title="Privacy Policy | Socialixer" description="Socialixer Privacy Policy — how we collect, use, and protect your data." path="/privacy" noIndex={false} /><Privacy /></>} />

            {/* Redirect Google OAuth Callback from /settings to /app/settings */}
            <Route path="/settings" element={
              <Navigate to={`/app/settings${window.location.search}`} replace />
            } />
            <Route path="/billing" element={
              <Navigate to={`/app/billing${window.location.search}`} replace />
            } />

            {/* Private Application Routes — protected by RequireAuth */}
            <Route path="/app/*" element={
              <RequireAuth>
                <FeatureToggleProvider>
                  <MainLayout isDarkMode={isDarkMode} onToggleTheme={toggleTheme}>
                    <Routes>
                      <Route path="/" element={
                        <><SEO title="Analytics Dashboard | Socialixer" description="Cross-platform social media analytics dashboard." path="/app" noIndex breadcrumbs={[{ name: 'Dashboard', path: '/app' }]} />
                          <Dashboard analytics={analytics} loading={loading} onRefresh={fetchAnalytics} error={error} /></>
                      } />
                      <Route path="/calendar" element={
                        <PostManagementPage onPost={handlePost} error={error} />
                      } />
                      <Route path="/post" element={
                        <PostManagementPage onPost={handlePost} error={error} />
                      } />
                      <Route path="/analyze" element={
                        <><SEO title="Analyse Content | Socialixer" description="Analyse your social media content for sentiment, readability, and engagement score." path="/app/analyze" noIndex breadcrumbs={[{ name: 'Analyse', path: '/app/analyze' }]} />
                          <AnalyzeContent onAnalyze={handleAnalyze} loading={loading} analysis={analysis} error={error} /></>
                      } />
                      <Route path="/rank" element={
                        <><SEO title="Rank Content | Socialixer" description="Rank your published posts by engagement score across all connected platforms." path="/app/rank" noIndex breadcrumbs={[{ name: 'Rank', path: '/app/rank' }]} />
                          <RankContent onRank={handleRank} loading={loading} ranked={ranked} error={error} /></>
                      } />
                      <Route path="/trends" element={
                        <><SEO title="Trending Now | Socialixer" description="Real-time trending topics from Google Trends to fuel your content strategy." path="/app/trends" noIndex breadcrumbs={[{ name: 'Trends', path: '/app/trends' }]} />
                          <TrendingNow /></>
                      } />
                      <Route path="/gaps" element={
                        <><SEO title="Content Gap Analysis | Socialixer" description="Discover content gaps between your posts and top-performing competitor content with AI analysis." path="/app/gaps" noIndex breadcrumbs={[{ name: 'Gap Analysis', path: '/app/gaps' }]} />
                          <FindGaps onFindGaps={handleGaps} loading={loading} gaps={gaps} error={error} /></>
                      } />
                      <Route path="/ideas" element={
                        <><SEO title="Generate Content Ideas | Socialixer" description="Generate AI-powered content ideas for Instagram, Twitter, LinkedIn and more using Google Gemini." path="/app/ideas" noIndex breadcrumbs={[{ name: 'Content Ideas', path: '/app/ideas' }]} />
                          <GenerateIdeas onGenerateIdeas={handleIdeas} loading={loading} idea={idea} error={error} /></>
                      } />
                      <Route path="/engagement" element={
                        <><SEO title="Engagement Hub | Socialixer" description="Manage automated social media engagement and comment automation." path="/app/engagement" noIndex breadcrumbs={[{ name: 'Engagement Hub', path: '/app/engagement' }]} />
                          <EngagementHub /></>
                      } />
                      <Route path="/crm" element={
                        <><SEO title="Lead CRM | Socialixer" description="Manage social media leads detected from comments and messages." path="/app/crm" noIndex breadcrumbs={[{ name: 'Lead CRM', path: '/app/crm' }]} />
                          <LeadCRM /></>
                      } />
                      <Route path="/guide" element={
                        <><SEO title="Setup Guide | Socialixer" description="Step-by-step guide to connecting your social media platforms to Socialixer." path="/app/guide" noIndex breadcrumbs={[{ name: 'Guide', path: '/app/guide' }]} />
                          <Guide /></>
                      } />
                      <Route path="/settings" element={
                        <><SEO title="Platform Settings | Socialixer" description="Connect and manage your social media platform API credentials." path="/app/settings" noIndex breadcrumbs={[{ name: 'Settings', path: '/app/settings' }]} />
                          <Settings /></>
                      } />
                      <Route path="/pricing" element={
                        <><SEO title="Pricing & Plans | Socialixer" description="Upgrade your Socialixer plan" path="/app/pricing" noIndex breadcrumbs={[{ name: 'Pricing & Plans', path: '/app/pricing' }]} />
                          <Pricing /></>
                      } />
                      <Route path="/billing" element={
                        <><SEO title="Billing | Socialixer" description="Manage your subscription" path="/app/billing" noIndex breadcrumbs={[{ name: 'Billing', path: '/app/billing' }]} />
                          <Billing /></>
                      } />

                      {/* Admin Area */}
                      <Route path="/admin/toggles" element={
                        <RequireSuperAdmin>
                          <><SEO title="Admin | Socialixer" description="Super Admin features management." path="/app/admin/toggles" noIndex breadcrumbs={[{ name: 'Admin Toggles', path: '/app/admin/toggles' }]} />
                            <AdminToggles /></>
                        </RequireSuperAdmin>
                      } />
                    </Routes>
                  </MainLayout>
                </FeatureToggleProvider>
              </RequireAuth>
            } />
          </Routes>
          <UploadTracker />

          {window.location.pathname.startsWith('/app') && (
            <Fab
              color="primary"
              aria-label="refresh"
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.5)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 12px 24px -4px rgba(99, 102, 241, 0.6)',
                }
              }}
              onClick={fetchAnalytics}
            >
              <RefreshIcon />
            </Fab>
          )}

          <Snackbar
            open={!!successMessage}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              sx={{
                bgcolor: theme.palette.success.main,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          <UpgradeModal
            open={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;